
import { HttpClient } from '@angular/common/http';
import { Injectable, Renderer2, inject } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError, first, filter, switchMap } from 'rxjs/operators';
import { isNil } from 'lodash-es';

import { EnvironmentService } from './environment.service';
import { ScriptsLazyLoaderService } from './scripts-lazy-loader.service';
import { CustomerDetails } from '../../objects/checkout-request';
import { ThirdPartyBase } from './base/third-party-base';

export interface StripeChange extends stripe.elements.ElementChangeResponse {
  card?: stripe.elements.Element;
  elements?: stripe.elements.Element[];
}

@Injectable({
  providedIn: 'root',
})
export class StripeService extends ThirdPartyBase {
  protected http = inject(HttpClient);
  protected environmentService: EnvironmentService;
  protected scriptsLazyLoaderService: ScriptsLazyLoaderService;

  private stripeElements?: stripe.elements.Elements;
  private stripe?: stripe.Stripe;

  constructor() {
    const environmentService = inject(EnvironmentService);
    const scriptsLazyLoaderService = inject(ScriptsLazyLoaderService);

    super(
      scriptsLazyLoaderService,
      environmentService,
      {
        ['stripe']: {
          name: 'stripe',
          src: 'https://js.stripe.com/v3/',
          loaded: false,
        },
      },
    );
  
    this.environmentService = environmentService;
    this.scriptsLazyLoaderService = scriptsLazyLoaderService;
  }

  get elements() {
    return this.stripeElements;
  }

  init(renderer2: Renderer2) {
    this.scriptsLazyLoaderService
      .load(this.scripts.stripe, renderer2)
      .subscribe(
        () => {
          
          const Stripe: stripe.StripeStatic = (window as any).Stripe;

          if (isNil(Stripe)) {
            this.isInitializedSubject.next(false);
            return;
          }

          const environment = this.environmentService.resolve();

          this.stripe = Stripe(environment.stripeKey!);
          this.stripeElements = this.stripe.elements();

          this.isInitializedSubject.next(true);
          
        },
        () => {
          this.isInitializedSubject.next(false);
        },
      );
  }

  createCard(
    opts?: stripe.elements.ElementsOptions
  ): Observable<StripeChange> {
    
    const style = {
      base: {
        color: '#363636',
        fontWeight: '400',
        fontSize: '16px',
        fontFamily: 'Nunito Sans,sans-serif',
        fontSmoothing: 'antialiased',

        '::placeholder': {
          color: '#D7D8DB',
          fontWeight: '300',
          fontSize: '16px',
          fontFamily: 'Nunito Sans,sans-serif',
          fontSmoothing: 'antialiased',
        },
        ':-webkit-autofill': {
          color: '#e39f48',
        },
      },
      invalid: {
        color: '#F26C66',
        '::placeholder': {
          color: '#F26C66',
        },
      },
    };

    const classes = {
      focus: 'focused',
      empty: 'empty',
      invalid: 'invalid',
    };

    const defaults: stripe.elements.ElementsOptions = {
      style,
      classes
    };

    if (!this.stripeElements) {
      return throwError(() => new Error('Stripe Elements not initialized'));
    }

    return this.isInitializedSubject
      .pipe(
        first(),
        filter(initialized => initialized),
        map(() => {
          
          const cardNumber = this.stripeElements!.getElement('cardNumber') ?? this.stripeElements!.create('cardNumber', { ...defaults, ...opts });
          const cardExpiry = this.stripeElements!.getElement('cardExpiry') ?? this.stripeElements!.create('cardExpiry', { ...defaults, ...opts });
          const cardCvc = this.stripeElements!.getElement('cardCvc') ?? this.stripeElements!.create('cardCvc', { ...defaults, ...opts, placeholder: 'CVC' });
          const elements = [cardNumber, cardExpiry, cardCvc];

          return {
            brand: '',
            complete: false,
            empty: true,
            elements,
          } as StripeChange;
        }),
      );
  }

  createToken(card: stripe.elements.Element, customerDetails: CustomerDetails): Observable<stripe.TokenResponse> {
    if (!this.stripe) {
      return throwError(() => new Error('Stripe not initialized'));
    }

    return this.isInitializedSubject
      .pipe(
        first(),
        filter(initialized => initialized),
        switchMap(() => from(this.stripe!.createToken(card, customerDetails))
          .pipe(
            map((result: stripe.TokenResponse) => {
              if (result.error) {
                throw result.error;
              }

              return result;
            }),
            catchError(error => throwError(() => error)),
          ),
        ),
      );
  }
}
